import { researchWarning, hasUsableResearch } from './legalResearchHelpers.js'

export const RELEVANCE_DECISIONS = { Pertinent: 'Pertinente à questão', NotPertinent: 'Não pertinente à questão' }

export function candidateRankings(result, candidate) {
  const matches = (row) => (row.candidateId === candidate.candidateId || row.aliasCandidateIds?.includes(candidate.candidateId))
    && row.documentVersionId === candidate.documentVersionId && row.textHash === candidate.textHash
  const rows = (result?.preliminaryRanking?.candidates || []).filter(matches)
  const revised = (result?.revisedRanking?.candidates || []).filter(matches)
  return [...rows.filter((row) => !revised.some((value) => value.issueId === row.issueId)), ...revised]
    .sort((left, right) => left.issueId.localeCompare(right.issueId))
}

export function orderedResearchCandidates(result) {
  return (result?.search?.candidates || []).map((candidate, index) => {
    const relevant = candidateRankings(result, candidate).filter((row) => row.matchedIssues?.includes(row.issueId))
    const ranks = relevant.map((row) => row.rank).filter((rank) => Number.isInteger(rank) && rank > 0)
    return { candidate, index, rank: ranks.length ? Math.min(...ranks) : Infinity }
  }).sort((left, right) => left.rank - right.rank || left.index - right.index).map(({ candidate }) => candidate)
}

export function candidateIssues(result, candidate) {
  const rankings = candidateRankings(result, candidate)
  const ids = new Set([...(candidate.issueIds || []), ...rankings.map((row) => row.issueId),
    ...(result?.validations || []).filter((value) => value.candidateId === candidate.candidateId).map((value) => value.issueId)])
  return (result?.extraction?.issues || []).filter((issue) => ids.has(issue.issueId))
}

export function candidateFeedback(result, candidateId, issueId) {
  return (result?.relevanceFeedback || []).find((value) => value.candidateId === candidateId && value.issueId === issueId
    && Object.hasOwn(RELEVANCE_DECISIONS, value.decision)) || null
}

export function relevancePayload(job, candidateId, issueId, decision, notes = '') {
  if (!['Completed', 'Partial'].includes(job?.state) || !Number.isInteger(job.revision) || job.revision < 1
    || !job.result?.documents?.some((value) => value.candidateId === candidateId)
    || !job.result?.search?.candidates?.some((value) => value.candidateId === candidateId)
    || !job.result?.extraction?.issues?.some((value) => value.issueId === issueId)
    || !Object.hasOwn(RELEVANCE_DECISIONS, decision) || typeof notes !== 'string' || notes.length > 1000)
    throw new Error('Selecione uma questão e uma avaliação de pertinência de um documento obtido nesta pesquisa. A justificativa admite até 1.000 caracteres.')
  return { revision: job.revision, candidateId, issueId, decision, ...(notes.trim() ? { notes: notes.trim() } : {}) }
}

export function eligibilityAfterFeedback(eligibility, result, candidateId, issueId, decision) {
  if (decision !== 'NotPertinent') return eligibility
  const blocked = new Set((result?.validations || []).filter((value) => value.candidateId === candidateId && value.issueId === issueId)
    .map((value) => value.validationId))
  return Object.fromEntries(Object.entries(eligibility).filter(([validationId]) => !blocked.has(validationId)))
}

export function reusableValidation(job, currentKey, jobKey, eligibility, validation) {
  return Boolean(validation?.validationId && hasUsableResearch(job, currentKey, jobKey,
    { [validation.validationId]: eligibility[validation.validationId] }))
}

export function publicReusePayload(job, validationId, confirmed) {
  if (confirmed !== true || !['Completed', 'Partial'].includes(job?.state) || !Number.isInteger(job.revision) || job.revision < 1
    || !job.result?.validations?.some((value) => value.validationId === validationId))
    throw new Error('Confirme o documento oficial público revisado antes de incluí-lo na base compartilhada.')
  return { revision: job.revision, validationId, confirmPublicDocumentReuse: true }
}

const RELEVANCE_REASONS = {
  PARTIAL_CONCEPT_COVERAGE_DISCOVERY_ONLY: 'Correspondência parcial: candidato mantido para descoberta, sem prioridade automática de leitura.',
  REQUIRED_ISSUE_CONCEPT_MISSING: 'Conceito necessário da questão não encontrado.',
  DOCUMENTED_CONTEXT_INCOMPATIBLE: 'A avaliação documental aponta contexto incompatível com esta questão.',
  CONTEXTUAL_FEEDBACK_TIEBREAK_ONLY: 'Sua avaliação anterior orientou o desempate da prioridade de leitura; a autorização para citar continua dependendo da conferência deste caso.',
  CONTEXTUAL_FEEDBACK_INVALID_OR_DUPLICATED_IGNORED: 'Uma avaliação duplicada ou incompatível foi desconsiderada na ordenação.',
  SUBSTANTIVE_RELEVANCE_NOT_ESTABLISHED: 'Não foi estabelecida pertinência substantiva a esta questão.',
  ISSUE_RELEVANCE_NOT_ESTABLISHED: 'A pertinência a esta questão ainda precisa ser estabelecida.',
  PROVIDER_DISCOVERY_IS_NOT_SUBSTANTIVE_RELEVANCE: 'A descoberta pela fonte, isoladamente, não confirma pertinência.',
  PROCEDURAL_CONTEXT_NOT_ASSESSED: 'O contexto processual ainda precisa ser comparado.',
  AUTHORITY_SCOPE_REQUIRES_LEGAL_VALIDATION: 'O alcance do precedente exige conferência jurídica.',
  AUTHORITY_SCOPE_INCOMPATIBLE: 'O alcance documentado é incompatível com este caso.',
  FACT_CORRESPONDENCE_NOT_ESTABLISHED: 'A correspondência entre os fatos ainda não foi estabelecida.',
  FACTUAL_CONTRADICTION_OR_OPPOSITE_NEGATION: 'Há diferença ou contradição nos fatos que exige exame.',
  COUNTERARGUMENT_REVIEW_RESERVED: 'Esta leitura foi reservada para examinar entendimento contrário.',
  DIVERSIFIED_ROUND_ROBIN_REVIEW_PRIORITY: 'Documento priorizado para leitura, considerando as questões e a diversidade de fontes.',
  TOPK_GLOBAL_BUDGET_OR_INCIDENT_DIVERSITY_LIMIT: 'Documento fora da prioridade de leitura nesta execução; continua disponível para exame.',
  RELEVANT_QUALIFIED_AUTHORITY_REMAINS_VISIBLE: 'Precedente qualificado pertinente mantido visível para conferência.',
  CLIENT_THESIS_NOT_KNOWN: 'A tese do cliente ainda não está identificada nas fontes.',
  RELATED_THEME_IS_NOT_THIS_DECISIONS_MERITS: 'A proximidade de um tema relacionado não confirma o mérito desta decisão.',
  OUTSIDE_REFERENCE_DATE: 'Documento posterior à data de referência da pesquisa.',
  DOCUMENT_AFTER_REFERENCE_DATE: 'Documento posterior à data de referência da pesquisa.',
}

export function relevanceReason(value) {
  const [code, ...parts] = String(value || '').split(':')
  const term = parts.join(':').trim()
  if (code === 'REQUIRED_ISSUE_CONCEPT_MISSING' && term)
    return `Conceito necessário da questão não encontrado: ${term}.`
  return RELEVANCE_REASONS[code] || researchWarning(value)
}
